import { ExprForm } from '@mode-switch/express';
import { EncChannel, Encoding, ExprFrame } from '@mode-vegas/vegas';
import { CompiledFormula } from '@mode/shared/util-switch';

export enum Type {
  TableAcross,
  TableDown,
  TableAcrossDown,
  TableDownAcross,
  PaneAcross,
  PaneDown,
  PaneAcrossDown,
  PaneDownAcross,
  Cell,
  Custom,
}

export function fromExprFrame(frame: ExprFrame): Type {
  switch (frame.type) {
    case ExprFrame.Type.Cell:
      return Type.Cell;
    case ExprFrame.Type.Custom:
      return Type.Custom;
    case ExprFrame.Type.Table: {
      switch (frame.address) {
        case ExprFrame.Address.Across:
          return Type.TableAcross;
        case ExprFrame.Address.Down:
          return Type.TableDown;
        case ExprFrame.Address.AcrossDown:
          return Type.TableAcrossDown;
        case ExprFrame.Address.DownAcross:
          return Type.TableDownAcross;
        default: {
          // @ts-expect-error Property 'address' does not exist on type 'never'
          throw new Error(`Unexpected Table frame address: ${frame.address}`);
        }
      }
    }
    case ExprFrame.Type.Pane: {
      switch (frame.address) {
        case ExprFrame.Address.Across:
          return Type.PaneAcross;
        case ExprFrame.Address.Down:
          return Type.PaneDown;
        case ExprFrame.Address.AcrossDown:
          return Type.PaneAcrossDown;
        case ExprFrame.Address.DownAcross:
          return Type.PaneDownAcross;
        default: {
          // @ts-expect-error Property 'address' does not exist on type 'never'
          throw new Error(`Unexpected Pane frame address: ${frame.address}`);
        }
      }
    }
  }
}

export function makeExprFrame(type: Type, dimensions: string[] = []): ExprFrame {
  switch (type) {
    case Type.Cell:
      return ExprFrame.Cell.make();
    case Type.Custom:
      return ExprFrame.Custom.make(dimensions);
    case Type.TableAcross:
      return ExprFrame.Table.make(ExprFrame.Address.Across);
    case Type.TableDown:
      return ExprFrame.Table.make(ExprFrame.Address.Down);
    case Type.TableAcrossDown:
      return ExprFrame.Table.make(ExprFrame.Address.AcrossDown);
    case Type.TableDownAcross:
      return ExprFrame.Table.make(ExprFrame.Address.DownAcross);
    case Type.PaneAcross:
      return ExprFrame.Pane.make(ExprFrame.Address.Across);
    case Type.PaneDown:
      return ExprFrame.Pane.make(ExprFrame.Address.Down);
    case Type.PaneAcrossDown:
      return ExprFrame.Pane.make(ExprFrame.Address.AcrossDown);
    case Type.PaneDownAcross:
      return ExprFrame.Pane.make(ExprFrame.Address.DownAcross);
    default: {
      throw new Error(`Cannot create frame of unknown type: "${type}"`);
    }
  }
}

// Orient the calculation in a way that makes sense for the visualization
export function makeDefaultExprFrame(encoding: Encoding, field: CompiledFormula, channel?: EncChannel) {
  if (!field.isAnalyticCalc) {
    return undefined;
  }
  //  As general rules, we want to keep this as simple as possible:
  //  1. Only use Table Across or Table Down
  //  2. Follow the encoding direction ('*' | 'y' = Across, 'x' = Down)

  if (channel === 'row' || channel === 'column') {
    if (channel === 'row') {
      // For most cases with the analytic field on y, we want 'Table Across'.
      // Except return Down if:
      //  - This is a y series, but there is nothing on x, then this is a
      //    vertically faceted chart so use 'Table Down'.
      const nX = encoding.x.length + encoding.x2.length;
      if (encoding.marks.channel === 'y' && encoding.row.length > 0 && nX === 0) {
        return makeExprFrame(Type.TableDown);
      }
    } else if (channel === 'column') {
      // Adding an analytic field to x has more potential to be 'Table Down'.
      // Return Across if:
      //  - This is an x series, but there is nothing on y, then this is a
      //    horizontally faceted chart so use 'Table Across'.
      const nY = encoding.y.length + encoding.y2.length;
      if (encoding.marks.channel === 'x' && encoding.column.length > 0 && nY === 0) {
        return makeExprFrame(Type.TableAcross);
      }
      return makeExprFrame(Type.TableDown);
    }
    // All other cases return Across
    return makeExprFrame(Type.TableAcross);
  } else if (field?.form === ExprForm.PctOfTotal) {
    // For PctOfTotal the user is typically trying to create a 100% chart. This requires the opposite
    // frame direction as other analytic function like RUNNING_SUM.
    const nRow = encoding.row.length;
    const nCol = encoding.column.length;
    const across = nRow > 0 && nCol > 0 ? makeExprFrame(Type.PaneAcross) : makeExprFrame(Type.TableAcross);
    const down = nRow > 0 && nCol > 0 ? makeExprFrame(Type.PaneDown) : makeExprFrame(Type.TableDown);
    return encoding.marks.channel === 'x' ? across : down;
  }
  return encoding.marks.channel === 'x' ? makeExprFrame(Type.TableDown) : makeExprFrame(Type.TableAcross);
}
